import { PageLoading } from "@ant-design/pro-layout";
import { type FC, lazy, Suspense, useRef } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import auhtRoutes from "./auth";
import type { IRoute } from "./types";
import { useStore } from "../contexts";
import dashboardRoutes from "./dashboard";
import ErrorBoundary from "./components/error-boundary";

const AuthLayout = lazy(() => import("../layouts/auth"));
const DashboardLayout = lazy(() => import("../layouts/dashboard"));
const NotAuthorized = lazy(() => import("../pages/exceptions/NotAuthorized"));
const NotFound = lazy(() => import("../pages/exceptions/NotFound"));

const MainRoutes: FC = () => {
  const location = useLocation();
  const nodeRef = useRef(null);
  const [{ authorized }] = useStore();

  const routes: IRoute[] = [
    {
      path: "/auth",
      key: "auth",
      component: <AuthLayout />,
      children: auhtRoutes,
    },
    {
      path: "/403",
      key: "not_authorized",
      component: <NotAuthorized />,
    },
    {
      key: "not_found",
      path: "*",
      component: <NotFound />,
    },
  ];

  if (authorized) {
    routes.push({
      path: "dashboard",
      key: "dashboard",
      component: <DashboardLayout />,
      children: dashboardRoutes,
    });
  }

  return (
    <TransitionGroup>
      <CSSTransition
        key={location.key}
        classNames="fade"
        timeout={300}
        nodeRef={nodeRef}
      >
        <div ref={nodeRef}>
          <Routes location={location}>
            <Route
              key="root"
              path="/"
              element={
                authorized ? (
                  <Navigate to={"/dashboard/home/carousel"} />
                ) : (
                  <Navigate to={"/auth/login"} />
                )
              }
            />
            {routes.map((route) =>
              !route.children ? (
                <Route
                  key={route.key}
                  path={route.path}
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<PageLoading />}>
                        {route.component}
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
              ) : (
                <Route
                  key={route.key}
                  path={route.path}
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<PageLoading />}>
                        {route.component}
                      </Suspense>
                    </ErrorBoundary>
                  }
                >
                  {route.children.map((item) => (
                    <Route
                      key={item.key}
                      path={item.path}
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoading />}>
                            {item.component}
                          </Suspense>
                        </ErrorBoundary>
                      }
                    />
                  ))}
                </Route>
              )
            )}
          </Routes>
        </div>
      </CSSTransition>
    </TransitionGroup>
  );
};

export default MainRoutes;
